import {
  addItemToCart,
  doAgeVerification,
  updateButtonState
} from '@/js/add-to-cart.js';

import { addToBundle } from '@/js/bundle-store';
import DynamicSectionElement from '@/js/dynamic-section-element';
import { initCountdownTimer } from '@/js/countdown-timer-actions.js';
import { getDelta } from '@/js/countdown-timer-actions.js';
import { maxQuantityHandler } from '@/js/utils/cart-utilities.js';
import { errorMessage } from '@/js/utils/error-message.js';

class ProductCardQuickAdd extends DynamicSectionElement {
  static get observedAttributes() {
    return ['ctaText'];
  }

  connectedCallback() {
    this.setup();
  }

  /**
   * setup the component
   */
  setup() {
    this.initVariables();
    this.addToCart();
    initCountdownTimer(this);
  }

  /**
   * Initializes the variables
   * @returns {void}
   */
  initVariables() {
    this.button = this.querySelector('button');
    this.countdownTranslations = globalThis.PxlApp.translations.countdownTimer;
    this.launchDate = this.button.dataset.launchDate;
    this.delta = getDelta(this.launchDate);
  }

  /**
   * Disables the button and updates the text
   */
  disableButton() {
    this.button.disabled = true;
    this.setTextBasedOnButtonState(true);
  }

  /**
   * Enables the button and updates the text
   */
  enableButton() {
    this.button.removeAttribute('disabled');
    this.setTextBasedOnButtonState(false);
  }

  /**
   * Sets the text based on the button state
   * @param {boolean} isDisabled
   */
  setTextBasedOnButtonState(isDisabled) {
    const { comingSoonText, addText } = this.countdownTranslations;
    const ctaText = this.button.dataset.buttonText || addText;

    this.setText(ctaText);

    if (isDisabled && this.delta > 0) {
      this.setText(comingSoonText);
    }
  }

  /**
   *  Sets the text of the CTA button
   * @param {string} newText
   */
  setText(newText) {
    this.setAttribute('ctaText', newText);
    this.updateCtaText(newText);
  }

  /**
   * Updates the text of the CTA button
   * @param {string} newText - The new text for the CTA button
   */
  updateCtaText(newText) {
    this.button.innerHTML = newText;
  }

  async addToCart() {
    const variantId = this.dataset.variantId;
    const quantity = Number(this.dataset.quantity || 1);
    const bundleable = Object.hasOwn(this.dataset, 'bundleable');
    const imageUrl = this.dataset.imageUrl;
    const title = this.dataset.title;
    const kyotoOnlyProduct = this.button.hasAttribute('kyoto-only-product');

    // Get the max inventory quantity from the button and check the requested quantity against it - if it's greater, set the quantity to the max inventory quantity
    const maxInventoryQuantity = Number(
      this.button.getAttribute('max-inventory-quantity')
    );
    const trackQuantity = Object.hasOwn(this.button.dataset, 'trackQuantity');

    this.addEventListener('click', async event => {
      await customElements.whenDefined('age-verification');
      const ageIsVerified = await doAgeVerification();
      if (!ageIsVerified) return;

      const quantityToAdd = await maxQuantityHandler(
        quantity,
        maxInventoryQuantity,
        variantId,
        trackQuantity,
        bundleable
      );

      // Don't add the item to the cart if the quantityToAdd is 0 or less
      if (quantityToAdd <= 0) {
        const button = event.target;
        const errorMessageWrapper = button.nextElementSibling;
        const errorContent = errorMessageWrapper.querySelector(
          '[data-error-message]'
        );
        await updateButtonState(this.button, 'error');
        const { alreadyAddedText } =
          globalThis.PxlApp.translations.productErrors;

        if (maxInventoryQuantity) {
          const message = alreadyAddedText.replace(
            '{quantity}',
            maxInventoryQuantity
          );
          await errorMessage(errorContent, message);
        }

        return;
      }

      if (bundleable) {
        try {
          // Check the bundle item quantity against the max inventory quantity
          const status = await addToBundle({
            variantId,
            quantity,
            title,
            imageUrl,
            maxInventoryQuantity,
            kyotoOnlyProduct,
            trackQuantity
          });

          status
            ? await updateButtonState(this.button, 'success')
            : await updateButtonState(this.button, 'error');
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      } else {
        try {
          const errorMessageWrapper =
            this.button.closest('button')?.nextElementSibling;
          const errorContent = errorMessageWrapper.querySelector(
            '[data-error-message]'
          );
          await addItemToCart(variantId, quantity, this.button, errorContent, [
            this.sectionId
          ]);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    });
  }
}

customElements.get('product-card-quick-add') ||
  customElements.define('product-card-quick-add', ProductCardQuickAdd);
